import React, { useMemo, useState } from "react";
import { Button, Card, Col, Modal, Row, Table } from "@themesberg/react-bootstrap";
import moment from "moment";
import { formatRewardsList, getRegionFromChannel, parseRewardNumbers } from "../../../../helpers/classicRewards";
import ImagePreview from "../../order/partials/image-preview/ImagePreview";

const formatDateTime = (value) => {
  if (!value) return "";
  const m = moment(value);
  if (!m.isValid()) return String(value);
  return m.format("DD/MM/YYYY HH:mm");
};

const formatText = (value) => {
  if (value === null || value === undefined) return "";
  return String(value);
};

const formatCurrencyVnd = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) return String(value);
  return new Intl.NumberFormat("vn-VI", {}).format(numberValue);
};

const prettifyJson = (value) => {
  try {
    return JSON.stringify(value, null, 2);
  } catch (e) {
    return String(value);
  }
};

const getStatusInfo = (status) => {
  switch (status) {
    case 1:
      return { text: "Đã mua", color: "info" };
    case 2:
      return { text: "Đang chụp", color: "warning" };
    case 3:
      return { text: "Đã xuất vé", color: "primary" };
    case 4:
      return { text: "Trúng thưởng", color: "success" };
    case 5:
      return { text: "Không trúng", color: "secondary" };
    default:
      return { text: "Hủy", color: "danger" };
  }
};

export default function TicketDetailModal({ show, onHide, ticket }) {
  const [showRawJson, setShowRawJson] = useState(false);
  const [imagePreviewShow, setImagePreviewShow] = useState(false);

  const viewModel = useMemo(() => {
    if (!ticket) return null;
    const statusInfo = getStatusInfo(ticket.status);
    const region = getRegionFromChannel(ticket.ClassicLotteryChannel);
    const parsedRewards = parseRewardNumbers(ticket.rewardNumbers, region);
    const rewardsDisplay = formatRewardsList(parsedRewards);

    return {
      cartId: ticket.ClassicCartId || ticket.cartId || "",
      ticketId: ticket.ticketId || "",
      quantity: ticket.amount,
      statusText: statusInfo.text,
      statusColor: statusInfo.color,
      createdAt: formatDateTime(ticket.createdAt),
      playerName: ticket.user?.name || "",
      playerPhone: ticket.user?.phone || "",
      numbers: ticket.ClassicLotteryStorage?.numbers || "",
      imageUrl: ticket.ClassicLotteryStorage?.images || "",
      channelName: ticket.ClassicLotteryChannel?.name || "",
      channelLogo: ticket.ClassicLotteryChannel?.logo || "",
      resultDate: ticket.ClassicResult?.resultDate || "",
      resultId: ticket.ClassicResult?.id,
      rewardAmount: ticket.rewardAmount,
      rewardsDisplay: rewardsDisplay,
    };
  }, [ticket]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      scrollable
      aria-labelledby="ticket-detail-modal-title"
    >
      <Modal.Header closeButton>
        <Modal.Title id="ticket-detail-modal-title">
          Chi tiết vé {viewModel?.ticketId ? `- ${viewModel.ticketId}` : ""}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {!ticket || !viewModel ? (
          <div className="text-muted">Không có dữ liệu để hiển thị</div>
        ) : (
          <>
            <Row className="g-3">
              <Col md={6}>
                <Card border="light" className="shadow-sm h-100">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <div>Thông tin vé</div>
                    <div className={`badge bg-${viewModel.statusColor}`}>
                      {viewModel.statusText}
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <Table responsive bordered size="sm" className="mb-0">
                      <tbody>
                        <tr>
                          <td style={{ width: 160 }}>Mã đơn</td>
                          <td style={{ wordBreak: "break-word" }}>
                            {formatText(viewModel.cartId)}
                          </td>
                        </tr>
                        <tr>
                          <td>Mã vé</td>
                          <td style={{ wordBreak: "break-word" }}>
                            {formatText(viewModel.ticketId)}
                          </td>
                        </tr>
                        <tr>
                          <td>Số lượng vé</td>
                          <td>{formatText(viewModel.quantity)}</td>
                        </tr>
                        <tr>
                          <td>Giải trúng</td>
                          <td>{viewModel.statusText === "Trúng thưởng" ? viewModel.rewardsDisplay : "-"}</td>
                        </tr>
                        <tr>
                          <td>Tiền trúng</td>
                          <td>
                            {viewModel.statusText === "Trúng thưởng"
                              ? formatCurrencyVnd(viewModel.rewardAmount)
                              : "-"}
                          </td>
                        </tr>
                        <tr>
                          <td>Ngày mua</td>
                          <td>{formatText(viewModel.createdAt)}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={6}>
                <Card border="light" className="shadow-sm h-100">
                  <Card.Header>Người chơi</Card.Header>
                  <Card.Body>
                    <Table responsive bordered size="sm" className="mb-0">
                      <tbody>
                        <tr>
                          <td style={{ width: 160 }}>Tên</td>
                          <td style={{ wordBreak: "break-word" }}>
                            {formatText(viewModel.playerName)}
                          </td>
                        </tr>
                        <tr>
                          <td>SĐT</td>
                          <td style={{ wordBreak: "break-word" }}>
                            {formatText(viewModel.playerPhone)}
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={12}>
                <Card border="light" className="shadow-sm">
                  <Card.Header>Thông tin quay số</Card.Header>
                  <Card.Body>
                    <Row className="align-items-center">
                      <Col md={8}>
                        <div style={{ fontSize: "1.1rem", letterSpacing: "2px" }}>
                          <strong className="text-primary">
                            {(viewModel.numbers || "").split("").join(" ")}
                          </strong>
                        </div>
                        <div className="text-muted">
                          {viewModel.channelName
                            ? `${viewModel.channelName}${
                                viewModel.resultDate ? ` - ${viewModel.resultDate}` : ""
                              }`
                            : ""}
                        </div>
                      </Col>
                      <Col md={4} className="text-md-end mt-2 mt-md-0">
                        {viewModel.channelLogo ? (
                          <img
                            src={viewModel.channelLogo}
                            alt={viewModel.channelName}
                            className="rounded border border-gray-200"
                            style={{ width: "52px", height: "52px", objectFit: "cover" }}
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        ) : null}
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>

              {viewModel.imageUrl && (
                <Col md={12}>
                  <Card border="light" className="shadow-sm">
                    <Card.Header>Hình ảnh vé</Card.Header>
                    <Card.Body className="text-center">
                      <div
                        className="rounded overflow-hidden d-inline-block shadow-sm"
                        style={{ width: 200, height: 200, cursor: "pointer" }}
                        onClick={() => setImagePreviewShow(true)}
                      >
                        <img
                          src={viewModel.imageUrl}
                          alt="Ticket"
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          onError={(e) => {
                            e.target.parentElement.style.display = "none";
                          }}
                        />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              )}

              {showRawJson && (
                <Col md={12}>
                  <Card border="light" className="shadow-sm">
                    <Card.Header>Raw JSON</Card.Header>
                    <Card.Body>
                      <pre
                        className="mb-0"
                        style={{
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          fontSize: "0.85rem",
                        }}
                      >
                        {prettifyJson(ticket)}
                      </pre>
                    </Card.Body>
                  </Card>
                </Col>
              )}
            </Row>
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between">
        <div>
          {viewModel?.imageUrl && (
            <Button
              variant="outline-primary"
              className="me-2"
              onClick={() => setImagePreviewShow(true)}
            >
              Xem ảnh
            </Button>
          )}
          <Button
            variant={showRawJson ? "primary" : "outline-primary"}
            onClick={() => setShowRawJson((v) => !v)}
          >
            {showRawJson ? "Ẩn JSON" : "Xem JSON"}
          </Button>
        </div>
        <Button variant="secondary" onClick={onHide}>
          Đóng
        </Button>
      </Modal.Footer>

      <ImagePreview
        show={imagePreviewShow}
        title={`Ảnh vé ${viewModel?.ticketId || ""}`}
        image={viewModel?.imageUrl || ""}
        onHide={() => setImagePreviewShow(false)}
      />
    </Modal>
  );
}
